import { type CSSProperties, useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { ArrowLeft, ChevronLeft, ChevronRight, FileDown, Loader2 } from "lucide-react"
import { toast } from "sonner"

import { Alert, AlertDescription } from "shared/ui/alert"
import { Badge } from "shared/ui/badge"
import { Button } from "shared/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "shared/ui/card"

import type {
  PreviewReportDatasourceResult,
  ReportLayoutWidgetDto,
  ReportRunFilterInput,
  ReportTemplateDatasourceDto,
  ReportTemplateDetailDto,
  ReportTemplateDto,
} from "modules/admin/api/reportTemplateService"
import {
  getReportTemplate,
  mergeSystemReportFields,
  runReport,
  searchReportTemplates,
} from "modules/admin/api/reportTemplateService"
import type { PreviewBodyRenderEntry } from "modules/admin/components/dynamic-report-editor/layoutTypes"
import { buildPreviewBodyPageEntries } from "modules/admin/components/dynamic-report-editor/previewLayoutRuntime"
import {
  DEFAULT_FLOW_SECTION_ID,
  TABLE_ROW_NO_KEY,
  WORKER_TABLE_COLUMNS,
  buildWidgetBorderStyle,
  getCanvasMetrics,
  getLayoutFlowSections,
  getLayoutManualPageCount,
  getWidgetCornerRadius,
  getWidgetImageFit,
  getWidgetLineConfig,
  getWidgetPositionMode,
  getWidgetTextAlign,
  getWidgetTypographyConfig,
  getWidgetVerticalAlign,
  isLikelyImageUrl,
  estimateFieldRenderedHeight,
  estimateTableRenderedHeight,
  normalizeLayoutForEditor,
  normalizeLayoutFrame,
  parseTableColumnWidthMap,
  resolveWidgetLabel,
  type TableColumnWidthMap,
  type TableSourceMode,
} from "modules/admin/components/dynamic-report-editor/editorUtils"
import {
  getDatasourceFieldDisplay,
  getDatasourceFieldLabel,
  getDatasourceTableRows,
  loadReportDatasourceResults,
} from "modules/admin/components/dynamic-report-editor/reportDatasourceRuntime"
import {
  getPermitDetail,
  type PermitDetail,
  type PermitDetailWorker,
} from "../api/permitService"
import { exportPermitLayoutPdf, createPermitLayoutPdfBlob } from "../utils/layoutPdfExport"
import { DEFAULT_TENANT_ID } from "shared/utils/tenantContext"

const REPORT_TEMPLATE_PAGE_SIZE = 200

const normalizeKey = (value?: string | null) => {
  if (typeof value !== "string") return ""
  return value.trim().toLowerCase().replace(/[\s-]+/g, "_")
}

const getMatchingTemplates = (
  templates: ReportTemplateDto[],
  permitTypeCode: string,
  templateVersionId?: string | null,
) => {
  const normalizedPermitTypeCode = permitTypeCode.trim().toLowerCase()
  const normalizedTemplateVersionId = templateVersionId?.trim().toLowerCase() ?? ""
  const byTemplateVersion = templates.filter(
    (item) =>
      normalizedTemplateVersionId.length > 0 &&
      (item.sourceTemplateVersionId ?? "").trim().toLowerCase() === normalizedTemplateVersionId,
  )
  const byCode = templates.filter(
    (item) => item.code.trim().toLowerCase() === normalizedPermitTypeCode,
  )

  const ordered = [...byTemplateVersion, ...byCode]
  const seen = new Set<string>()
  return ordered.filter((item) => {
    if (!item.reportTemplateId || seen.has(item.reportTemplateId)) return false
    seen.add(item.reportTemplateId)
    return true
  })
}

const formatPreviewValue = (value: unknown): string => {
  if (value == null) return ""
  if (typeof value === "boolean") return value ? "Yes" : "No"
  if (typeof value === "object") return JSON.stringify(value)
  const normalized = String(value).trim()
  if (!normalized) return ""
  const lowered = normalized.toLowerCase()
  if (lowered === "null" || lowered === "undefined") return ""
  return normalized
}

const formatPreviewLabel = (value: string): string => {
  const normalized = value
    .replace(/^__/, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()

  if (!normalized) return "-"

  return normalized.replace(/\b\w/g, (char) => char.toUpperCase())
}

const getPermitFieldDisplayValue = (field: PermitDetail["fieldValues"][number]): string => {
  if (field.displayValue && field.displayValue.trim()) return formatPreviewValue(field.displayValue)
  if (field.valueText && field.valueText.trim()) return formatPreviewValue(field.valueText)
  if (field.valueDate && field.valueDate.trim()) return formatPreviewValue(field.valueDate)
  if (field.valueTimestamp && field.valueTimestamp.trim()) return formatPreviewValue(field.valueTimestamp)
  if (typeof field.valueNumber === "number" && !Number.isNaN(field.valueNumber)) {
    return formatPreviewValue(field.valueNumber)
  }
  if (typeof field.valueBoolean === "boolean") {
    return field.valueBoolean ? "Yes" : "No"
  }
  return ""
}

const buildReportFilters = (
  templateDetail: ReportTemplateDetailDto,
  permitId: string,
  permitNumber: string,
): ReportRunFilterInput[] => {
  const fieldKeys = new Set(
    (templateDetail.fields ?? []).map((field) => normalizeKey(field.fieldKey)),
  )
  const filterKeys = new Set(
    (templateDetail.filters ?? []).map((filter) => normalizeKey(filter.fieldKey)),
  )

  const canUsePermitNumberFilter =
    fieldKeys.has("permit_number") || filterKeys.has("permit_number")
  const canUsePermitIdFilter = fieldKeys.has("permit_id") || filterKeys.has("permit_id")

  if (canUsePermitNumberFilter) {
    return [{ fieldKey: "permit_number", operator: "=", value: permitNumber }]
  }
  if (canUsePermitIdFilter) {
    return [{ fieldKey: "permit_id", operator: "=", value: permitId }]
  }
  return [{ fieldKey: "permit_number", operator: "=", value: permitNumber }]
}

const hasLayoutWidgets = (templateDetail: ReportTemplateDetailDto | null) =>
  Boolean(
    templateDetail?.layoutJson &&
      ((templateDetail.layoutJson.widgets?.length ?? 0) > 0 ||
        (templateDetail.layoutJson.headerSection?.widgets?.length ?? 0) > 0 ||
        (templateDetail.layoutJson.footerSection?.widgets?.length ?? 0) > 0),
  )

const resolveWorkerColumnValue = (worker: PermitDetailWorker, columnKey: string): unknown => {
  if (columnKey === "companyName") {
    return (
      worker.companyName ||
      (worker as unknown as Record<string, unknown>).company ||
      (worker as unknown as Record<string, unknown>).orgName ||
      "-"
    )
  }
  if (columnKey === "jobRole") {
    return worker.jobRole || worker.roleInPermit || "-"
  }
  return (worker as unknown as Record<string, unknown>)[columnKey]
}

const getTableSource = (widget: ReportLayoutWidgetDto): TableSourceMode =>
  String(widget.props?.tableSource || "reportRows") === "workers"
    ? "workers"
    : String(widget.props?.tableSource || "reportRows") === "datasource"
      ? "datasource"
      : "reportRows"

const getTableColumnLabel = (
  widget: ReportLayoutWidgetDto,
  columnKey: string,
  fieldLabelMap: Map<string, string>,
  datasourcePreviewResults: Record<string, PreviewReportDatasourceResult>,
  datasources: ReportTemplateDatasourceDto[],
) => {
  if (columnKey === TABLE_ROW_NO_KEY) return "No."
  if (getTableSource(widget) === "workers") {
    return WORKER_TABLE_COLUMNS.find((item) => item.key === columnKey)?.label || columnKey
  }
  if (getTableSource(widget) === "datasource") {
    return getDatasourceFieldLabel(
      datasources ?? [],
      datasourcePreviewResults,
      String(widget.props?.datasourceId || ""),
      columnKey,
    )
  }
  return fieldLabelMap.get(columnKey) || columnKey
}

const getTableColumnWidths = (widget: ReportLayoutWidgetDto, columns: string[]): TableColumnWidthMap => {
  const map = parseTableColumnWidthMap(widget.props?.columnWidths)
  if (columns.length === 0) return {}
  const next: TableColumnWidthMap = {}
  columns.forEach((columnKey) => {
    const width = map[columnKey]
    if (Number.isFinite(width) && width > 0) {
      next[columnKey] = width
    }
  })
  return next
}

const readWidgetText = (
  widget: ReportLayoutWidgetDto,
  fieldLabelMap: Map<string, string>,
  getFieldDisplay: (fieldKey: string, datasourceId?: string) => string,
) => {
  if (widget.type === "text") {
    return formatPreviewValue(widget.props?.text)
  }
  if (widget.type === "field") {
    const fieldKey = String(widget.props?.fieldKey || "")
    const fieldLabel = resolveWidgetLabel(widget, fieldLabelMap.get(fieldKey) || "Field")
    const fieldValue = getFieldDisplay(fieldKey, String(widget.props?.datasourceId || ""))
    return { fieldLabel, fieldValue }
  }
  return null
}

interface PermitReportPreviewContentProps {
  permitId: string
  tenantId?: string
  onClose?: () => void
  closeLabel?: string
  renderPdfInline?: boolean
}

export function PermitReportPreviewContent({
  permitId,
  tenantId = DEFAULT_TENANT_ID,
  onClose,
  closeLabel = "Close",
  renderPdfInline = false,
}: PermitReportPreviewContentProps) {
  const resolvedTenantId = tenantId?.trim() || DEFAULT_TENANT_ID

  const [isLoading, setIsLoading] = useState(true)
  const [isExporting, setIsExporting] = useState(false)
  const [isPreparingPdf, setIsPreparingPdf] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [permitDetail, setPermitDetail] = useState<PermitDetail | null>(null)
  const [reportTemplate, setReportTemplate] = useState<ReportTemplateDto | null>(null)
  const [templateDetail, setTemplateDetail] = useState<ReportTemplateDetailDto | null>(null)
  const [reportRows, setReportRows] = useState<Array<Record<string, unknown>>>([])
  const [datasourcePreviewResults, setDatasourcePreviewResults] = useState<Record<string, PreviewReportDatasourceResult>>({})
  const [previewPageIndex, setPreviewPageIndex] = useState(0)
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null)

  const loadPreview = useCallback(async () => {
    if (!permitId.trim()) {
      setError("Missing permit id")
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)
    setPdfBlobUrl((current) => {
      if (current) {
        URL.revokeObjectURL(current)
      }
      return null
    })

    try {
      const detail = await getPermitDetail(resolvedTenantId, permitId)
      if (!detail) {
        throw new Error("Permit not found")
      }

      const permitTypeCode = String(detail.permitTypeCode ?? "").trim()
      if (!permitTypeCode) {
        throw new Error("Permit type code not found")
      }

      const templateResult = await searchReportTemplates({
        tenantId: resolvedTenantId,
        status: "active",
        page: 1,
        pageSize: REPORT_TEMPLATE_PAGE_SIZE,
      })

      const matchingTemplates = getMatchingTemplates(
        templateResult.items ?? [],
        permitTypeCode,
        detail.templateVersionId,
      )
      if (matchingTemplates.length === 0) {
        throw new Error(`No report template for permit type: ${permitTypeCode}`)
      }

      let matchedTemplate: ReportTemplateDto | null = null
      let detailTemplate: ReportTemplateDetailDto | null = null

      for (const candidate of matchingTemplates) {
        const candidateDetail = await getReportTemplate(
          candidate.reportTemplateId,
          resolvedTenantId,
        )

        if (!matchedTemplate) {
          matchedTemplate = candidate
          detailTemplate = candidateDetail
        }

        if (hasLayoutWidgets(candidateDetail)) {
          matchedTemplate = candidate
          detailTemplate = candidateDetail
          break
        }
      }

      if (!matchedTemplate || !detailTemplate) {
        throw new Error(`Cannot load report template for permit type: ${permitTypeCode}`)
      }

      const allFieldColumns = mergeSystemReportFields(detailTemplate.fields ?? [])
        .map((field) => field.fieldKey)
        .filter(Boolean)
      const filters = buildReportFilters(
        detailTemplate,
        detail.permitId,
        detail.permitNumber || detail.permitId,
      )

      const reportResult = await runReport({
        tenantId: resolvedTenantId,
        templateId: matchedTemplate.reportTemplateId,
        columns: allFieldColumns.length > 0 ? allFieldColumns : undefined,
        filters,
        pageNumber: 1,
        pageSize: 5000,
      })

      setPermitDetail(detail)
      setReportTemplate(matchedTemplate)
      setTemplateDetail(detailTemplate)
      setReportRows(reportResult.rows ?? [])
      setPreviewPageIndex(0)
    } catch (loadError) {
      console.error(loadError)
      const message =
        loadError instanceof Error ? loadError.message : "Cannot load report preview"
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }, [permitId, resolvedTenantId])

  useEffect(() => {
    void loadPreview()
  }, [loadPreview])

  useEffect(() => {
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl)
      }
    }
  }, [pdfBlobUrl])

  const layout = useMemo(() => {
    if (!templateDetail?.layoutJson) return null
    return normalizeLayoutForEditor(templateDetail.layoutJson)
  }, [templateDetail])
  const reportDatasources = useMemo(() => layout?.datasources ?? [], [layout])

  const previewEnabled = Boolean(layout) && hasLayoutWidgets(templateDetail)

  const fullCanvasMetrics = useMemo(
    () => getCanvasMetrics(layout?.orientation ?? "portrait"),
    [layout?.orientation],
  )

  const headerEnabled = Boolean(layout?.headerSection?.enabled)
  const footerEnabled = Boolean(layout?.footerSection?.enabled)
  const headerHeight = headerEnabled
    ? Math.max(60, Number(layout?.headerSection?.height || 120))
    : 0
  const footerHeight = footerEnabled
    ? Math.max(60, Number(layout?.footerSection?.height || 80))
    : 0
  const bodyOffsetY = headerHeight
  const footerOffsetY = fullCanvasMetrics.height - footerHeight
  const baseBodyCanvasHeight = Math.max(
    200,
    fullCanvasMetrics.height - headerHeight - footerHeight,
  )

  const flowSections = useMemo(
    () => (layout ? getLayoutFlowSections(layout) : []),
    [layout],
  )
  const manualPageCount = useMemo(
    () => (layout ? getLayoutManualPageCount(layout) : 1),
    [layout],
  )
  const primaryFlowSectionId =
    flowSections[0]?.sectionId || DEFAULT_FLOW_SECTION_ID

  const fieldLabelMap = useMemo(
    () =>
      new Map(
        mergeSystemReportFields(templateDetail?.fields ?? []).map((field) => [
          field.fieldKey,
          field.label || field.fieldKey,
        ]),
      ),
    [templateDetail?.fields],
  )

  const previewValueMap = useMemo(
    () =>
      new Map(
        Object.entries(reportRows[0] ?? {}).map(([key, value]) => [
          String(key),
          formatPreviewValue(value),
        ]),
      ),
    [reportRows],
  )

  useEffect(() => {
    if (!permitDetail || reportDatasources.length === 0) {
      setDatasourcePreviewResults({})
      return
    }

    let cancelled = false
    loadReportDatasourceResults({
      tenantId: resolvedTenantId,
      datasources: reportDatasources,
      row: reportRows[0] ?? null,
      permitDetail,
    })
      .then((result) => {
        if (!cancelled) {
          setDatasourcePreviewResults(result)
        }
      })
      .catch((datasourceError) => {
        if (!cancelled) {
          console.error(datasourceError)
          setDatasourcePreviewResults({})
        }
      })

    return () => {
      cancelled = true
    }
  }, [permitDetail, reportDatasources, reportRows, resolvedTenantId])

  const getFieldDisplay = useCallback(
    (fieldKey: string, datasourceId?: string) => {
      if (datasourceId) {
        return getDatasourceFieldDisplay(datasourcePreviewResults, datasourceId, fieldKey)
      }
      const value = previewValueMap.get(fieldKey)
      if (value && value.trim()) return value
      if (fieldKey === "permit_number" && permitDetail?.permitNumber) return formatPreviewValue(permitDetail.permitNumber)
      if (fieldKey === "permit_id" && permitDetail?.permitId) return formatPreviewValue(permitDetail.permitId)
      return ""
    },
    [datasourcePreviewResults, permitDetail?.permitId, permitDetail?.permitNumber, previewValueMap],
  )

  const workers = useMemo(() => permitDetail?.workers ?? [], [permitDetail?.workers])
  const attachments = useMemo(
    () => permitDetail?.attachments ?? [],
    [permitDetail?.attachments],
  )

  const fallbackSummaryEntries = useMemo(() => {
    if (!permitDetail) return []

    return [
      { label: "Permit Number", value: formatPreviewValue(permitDetail.permitNumber) },
      { label: "Status", value: formatPreviewValue(permitDetail.statusName || permitDetail.status) },
      { label: "Permit Type", value: formatPreviewValue(permitDetail.permitTypeName || permitDetail.permitTypeCode) },
      { label: "Title", value: formatPreviewValue(permitDetail.title) },
      { label: "Requester", value: formatPreviewValue(permitDetail.requesterName) },
      { label: "Company", value: formatPreviewValue(permitDetail.requesterCompany) },
      { label: "Planned Start", value: formatPreviewValue(permitDetail.plannedStart) },
      { label: "Planned End", value: formatPreviewValue(permitDetail.plannedEnd) },
    ].filter((entry) => entry.value.trim().length > 0)
  }, [permitDetail])

  const fallbackReportEntries = useMemo(() => {
    const row = reportRows[0] ?? null
    if (row) {
      const entries = Object.entries(row)
        .filter(([key]) => key !== "__permitId" && key !== "__permitNumber")
        .map(([key, value]) => ({
          label: formatPreviewLabel(key),
          value: formatPreviewValue(value),
        }))
        .filter((entry) => entry.value.trim().length > 0)

      if (entries.length > 0) return entries
    }

    return (permitDetail?.fieldValues ?? [])
      .map((field) => ({
        label: field.fieldCode,
        value: getPermitFieldDisplayValue(field),
      }))
      .filter((entry) => entry.value.trim().length > 0)
  }, [permitDetail, reportRows])

  const getTablePreviewRows = useCallback(
    (widget: ReportLayoutWidgetDto, columns: string[]) => {
      if (columns.length === 0) return []

      if (getTableSource(widget) === "workers") {
        return workers.map((worker: PermitDetailWorker, rowIndex) =>
          columns.map((columnKey) => {
            if (columnKey === TABLE_ROW_NO_KEY) return String(rowIndex + 1)
            const value = resolveWorkerColumnValue(worker, columnKey)
            return formatPreviewValue(value)
          }),
        )
      }

      if (getTableSource(widget) === "datasource") {
        return getDatasourceTableRows(
          datasourcePreviewResults,
          String(widget.props?.datasourceId || ""),
          columns.filter((columnKey) => columnKey !== TABLE_ROW_NO_KEY),
        ).map((row, rowIndex) =>
          columns.map((columnKey, columnIndex) => {
            if (columnKey === TABLE_ROW_NO_KEY) return String(rowIndex + 1)
            const safeIndex = columns.slice(0, columnIndex).filter((item) => item !== TABLE_ROW_NO_KEY).length
            return row[safeIndex] ?? ""
          }),
        )
      }

      if (reportRows.length === 0) return []
      return reportRows.map((row, rowIndex) =>
        columns.map((columnKey) => {
          if (columnKey === TABLE_ROW_NO_KEY) return String(rowIndex + 1)
          return formatPreviewValue(row[columnKey])
        }),
      )
    },
    [datasourcePreviewResults, reportRows, workers],
  )

  const previewBodyPageEntries = useMemo<Map<number, PreviewBodyRenderEntry[]>>(() => {
    if (!layout) return new Map<number, PreviewBodyRenderEntry[]>([[0, []]])
    const previewBodyWidgets = [...(layout.widgets ?? [])].sort(
      (a, b) => (a.zIndex || 0) - (b.zIndex || 0),
    )
    return buildPreviewBodyPageEntries({
      baseBodyCanvasHeight,
      flowSections,
      manualPageCount,
      previewBodyWidgets,
      primaryFlowSectionId,
      getTablePreviewRows,
      getWidgetMeasuredHeight: (widget) => {
        if (widget.type !== "field") return undefined
        const fieldText = readWidgetText(widget, fieldLabelMap, getFieldDisplay)
        const fieldLabel =
          fieldText && typeof fieldText === "object" ? fieldText.fieldLabel : "Field"
        const fieldValue =
          fieldText && typeof fieldText === "object" ? fieldText.fieldValue : ""
        return estimateFieldRenderedHeight(widget, fieldLabel, fieldValue)
      },
    })
  }, [
    baseBodyCanvasHeight,
    fieldLabelMap,
    flowSections,
    getFieldDisplay,
    getTablePreviewRows,
    layout,
    manualPageCount,
    primaryFlowSectionId,
  ])

  const previewTotalPages = useMemo(() => {
    const indices = Array.from(previewBodyPageEntries.keys())
    if (indices.length === 0) return Math.max(1, manualPageCount)
    return Math.max(1, manualPageCount, Math.max(...indices) + 1)
  }, [manualPageCount, previewBodyPageEntries])

  useEffect(() => {
    setPreviewPageIndex((prev) => Math.max(0, Math.min(prev, previewTotalPages - 1)))
  }, [previewTotalPages])

  const previewHeaderWidgets = useMemo(
    () =>
      layout
        ? [...(layout.headerSection?.widgets ?? [])].sort(
            (a, b) => (a.zIndex || 0) - (b.zIndex || 0),
          )
        : [],
    [layout],
  )

  const previewFooterWidgets = useMemo(
    () =>
      layout
        ? [...(layout.footerSection?.widgets ?? [])].sort(
            (a, b) => (a.zIndex || 0) - (b.zIndex || 0),
          )
        : [],
    [layout],
  )

  const previewBodyEntries = useMemo(
    () =>
      [...(previewBodyPageEntries.get(previewPageIndex) ?? [])].sort((a, b) => {
        const yDiff = a.renderY - b.renderY
        if (yDiff !== 0) return yDiff
        return (a.widget.zIndex || 0) - (b.widget.zIndex || 0)
      }),
    [previewBodyPageEntries, previewPageIndex],
  )

  const handleExportPdf = useCallback(async () => {
    if (!permitDetail || !reportTemplate || !templateDetail) return
    if (!hasLayoutWidgets(templateDetail)) {
      toast.error("Template does not have layout widgets")
      return
    }

    setIsExporting(true)
    try {
      const safePermitNumber = String(
        permitDetail.permitNumber || permitDetail.permitId,
      ).replace(/[^\w.-]+/g, "_")
      const usedThai = await exportPermitLayoutPdf({
        templateDetail,
        templateCode: reportTemplate.code,
        templateName: reportTemplate.name,
        permitNumber: permitDetail.permitNumber || permitDetail.permitId,
        rows: reportRows,
        workers: workers,
        fileName: `${safePermitNumber}.pdf`,
        tenantId: resolvedTenantId,
        permitDetail,
      })
      if (!usedThai) {
        toast.warning(
          "PDF exported without Thai font; verify Thai text rendering.",
        )
      } else {
        toast.success("PDF exported")
      }
    } catch (exportError) {
      console.error(exportError)
      const message =
        exportError instanceof Error
          ? exportError.message
          : "Cannot create PDF"
      toast.error(message)
    } finally {
      setIsExporting(false)
    }
  }, [permitDetail, reportRows, reportTemplate, resolvedTenantId, templateDetail, workers])

  const prepareInlinePdf = useCallback(async () => {
    if (!renderPdfInline || !permitDetail || !reportTemplate || !templateDetail) return
    if (!hasLayoutWidgets(templateDetail)) return

    setIsPreparingPdf(true)
    try {
      const safePermitNumber = String(
        permitDetail.permitNumber || permitDetail.permitId,
      ).replace(/[^\w.-]+/g, "_")
      const { blob, usedThaiFont } = await createPermitLayoutPdfBlob({
        templateDetail,
        templateCode: reportTemplate.code,
        templateName: reportTemplate.name,
        permitNumber: permitDetail.permitNumber || permitDetail.permitId,
        rows: reportRows,
        workers,
        fileName: `${safePermitNumber}.pdf`,
        tenantId: resolvedTenantId,
        permitDetail,
      })

      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl)
      }
      setPdfBlobUrl(URL.createObjectURL(blob))

      if (!usedThaiFont) {
        toast.warning("PDF preview generated without Thai font; verify Thai text rendering.")
      }
    } catch (pdfError) {
      console.error(pdfError)
      const message = pdfError instanceof Error ? pdfError.message : "Cannot create PDF preview"
      setError(message)
    } finally {
      setIsPreparingPdf(false)
    }
  }, [
    pdfBlobUrl,
    permitDetail,
    renderPdfInline,
    reportRows,
    reportTemplate,
    resolvedTenantId,
    templateDetail,
    workers,
  ])

  useEffect(() => {
    if (!renderPdfInline || !previewEnabled) return
    if (isLoading || isPreparingPdf || pdfBlobUrl || error) return
    void prepareInlinePdf()
  }, [error, isLoading, isPreparingPdf, pdfBlobUrl, prepareInlinePdf, previewEnabled, renderPdfInline])

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-10">
          <div className="flex items-center gap-2 text-slate-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading preview...
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Report Preview</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <div className="flex gap-2">
            {onClose ? <Button variant="outline" onClick={onClose}>{closeLabel}</Button> : null}
            <Button onClick={() => void loadPreview()}>Retry</Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!permitDetail || !reportTemplate || !templateDetail) {
    return (
      <Card>
        <CardContent className="p-6">No preview data</CardContent>
      </Card>
    )
  }

  if (!layout) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle className="text-lg">Permit Report Preview</CardTitle>
                <p className="text-sm text-slate-600">
                  {permitDetail.permitNumber} | {reportTemplate.name}
                </p>
                <p className="text-xs text-slate-500">
                  Rows: {reportRows.length} | Workers: {workers.length} | Template:{" "}
                  {reportTemplate.code}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {onClose ? (
                  <Button variant="outline" onClick={onClose}>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {closeLabel}
                  </Button>
                ) : null}
                <Button
                  onClick={() => void handleExportPdf()}
                  disabled
                  title="Export requires a configured report layout"
                >
                  <FileDown className="mr-2 h-4 w-4" />
                  Export PDF
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertDescription>
                Report template layout is not configured for this permit type yet. Showing raw
                permit data instead.
              </AlertDescription>
            </Alert>

            <div className="flex items-center justify-end">
              <Button variant="outline" size="sm" onClick={() => void loadPreview()}>
                Refresh
              </Button>
            </div>

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              {fallbackSummaryEntries.map((entry) => (
                <div key={entry.label} className="rounded-lg border bg-slate-50 p-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    {entry.label}
                  </p>
                  <p className="mt-1 text-sm text-slate-900 break-words">{entry.value}</p>
                </div>
              ))}
            </div>

            <div className="rounded-lg border">
              <div className="border-b bg-slate-50 px-4 py-3">
                <h3 className="text-sm font-semibold text-slate-900">Field Data</h3>
              </div>
              <div className="divide-y">
                {fallbackReportEntries.length > 0 ? (
                  fallbackReportEntries.map((entry) => (
                    <div
                      key={`${entry.label}-${entry.value}`}
                      className="grid gap-2 px-4 py-3 md:grid-cols-[240px_1fr]"
                    >
                      <div className="text-sm font-medium text-slate-600">{entry.label}</div>
                      <div className="text-sm text-slate-900 break-words">{entry.value}</div>
                    </div>
                  ))
                ) : (
                  <div className="px-4 py-3 text-sm text-slate-500">No field data available.</div>
                )}
              </div>
            </div>

            <div className="rounded-lg border">
              <div className="border-b bg-slate-50 px-4 py-3">
                <h3 className="text-sm font-semibold text-slate-900">Workers</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="border-b px-4 py-3 text-left font-medium">Name</th>
                      <th className="border-b px-4 py-3 text-left font-medium">Company</th>
                      <th className="border-b px-4 py-3 text-left font-medium">Role</th>
                      <th className="border-b px-4 py-3 text-left font-medium">Card No.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workers.length > 0 ? (
                      workers.map((worker) => (
                        <tr key={worker.permitWorkerId}>
                          <td className="border-b px-4 py-3 text-slate-900">{worker.fullName || "-"}</td>
                          <td className="border-b px-4 py-3 text-slate-700">{worker.companyName || "-"}</td>
                          <td className="border-b px-4 py-3 text-slate-700">{worker.roleInPermit || worker.jobRole || "-"}</td>
                          <td className="border-b px-4 py-3 text-slate-700">{worker.cardNumber || "-"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="px-4 py-3 text-slate-500">
                          No workers found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-lg border">
              <div className="border-b bg-slate-50 px-4 py-3">
                <h3 className="text-sm font-semibold text-slate-900">Attachments</h3>
              </div>
              <div className="divide-y">
                {attachments.length > 0 ? (
                  attachments.map((attachment) => (
                    <div
                      key={attachment.permitAttachmentId}
                      className="grid gap-2 px-4 py-3 md:grid-cols-[240px_1fr]"
                    >
                      <div className="text-sm font-medium text-slate-600">
                        {attachment.documentName || attachment.documentCode || "Attachment"}
                      </div>
                      <div className="text-sm text-slate-900 break-words">{attachment.fileName || "-"}</div>
                    </div>
                  ))
                ) : (
                  <div className="px-4 py-3 text-sm text-slate-500">No attachments found.</div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (renderPdfInline) {
    if (isPreparingPdf || !pdfBlobUrl) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-slate-100">
          <div className="flex items-center gap-2 text-slate-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            Preparing PDF...
          </div>
        </div>
      )
    }

    return (
      <iframe
        title="Permit Report PDF"
        src={pdfBlobUrl}
        className="block h-screen w-screen bg-white"
      />
    )
  }

  const frame = normalizeLayoutFrame(layout.frame)
  const frameTopOffset = frame.scope === "body" ? bodyOffsetY : 0
  const frameHeightScope =
    frame.scope === "body"
      ? Math.max(40, fullCanvasMetrics.height - bodyOffsetY - footerHeight)
      : fullCanvasMetrics.height

  const renderWidget = (
    widget: ReportLayoutWidgetDto,
    yOffset: number,
    renderYOverride?: number,
    renderHeightOverride?: number,
    tableRows?: string[][],
  ) => {
    const safeX = Number(widget.x || 0)
    const safeY = Number.isFinite(renderYOverride) ? Number(renderYOverride) : Number(widget.y || 0)
    const safeWidth = Math.max(1, Number(widget.width || 1))
    const safeHeight = Math.max(1, Number.isFinite(renderHeightOverride) ? Number(renderHeightOverride) : Number(widget.height || 1))
    const baseStyle = {
      left: safeX,
      top: safeY + yOffset,
      width: safeWidth,
      height: safeHeight,
      zIndex: widget.zIndex || 1,
      borderRadius: getWidgetCornerRadius(widget),
      ...buildWidgetBorderStyle(widget),
    } as CSSProperties

    const textAlign = getWidgetTextAlign(widget)
    const verticalAlign = getWidgetVerticalAlign(widget)
    const typography = getWidgetTypographyConfig(widget)
    const justifyByHorizontal =
      textAlign === "center"
        ? "center"
        : textAlign === "right"
          ? "flex-end"
          : "flex-start"
    const alignByVertical =
      verticalAlign === "center"
        ? "center"
        : verticalAlign === "bottom"
          ? "flex-end"
          : "flex-start"
    const contentPadding = Math.max(0, Math.min(4, Math.floor(Math.min(safeWidth, safeHeight) / 10)))
    const typographyStyle = {
      fontSize: typography.fontSize,
      fontWeight: typography.fontWeight,
      fontStyle: typography.fontStyle,
      textAlign,
    } as CSSProperties

    if (widget.type === "text") {
      return (
        <div
          key={`preview-${widget.id}`}
          className="absolute overflow-hidden bg-white text-sm text-slate-900"
          style={{
            ...baseStyle,
            display: "flex",
            alignItems: alignByVertical,
            justifyContent: justifyByHorizontal,
            padding: contentPadding,
          }}
        >
          <div className="whitespace-pre-wrap break-words w-full" style={typographyStyle}>
            {formatPreviewValue(widget.props?.text)}
          </div>
        </div>
      )
    }

    if (widget.type === "field") {
      const fieldText = readWidgetText(widget, fieldLabelMap, getFieldDisplay)
      const fieldLabel =
        fieldText && typeof fieldText === "object" ? fieldText.fieldLabel : "Field"
      const fieldValue =
        fieldText && typeof fieldText === "object" ? fieldText.fieldValue : "-"
      const hasFieldLabel = fieldLabel.trim().length > 0
      const isFlowField = getWidgetPositionMode(widget) === "flow"
      const renderedFieldHeight = estimateFieldRenderedHeight(widget, fieldLabel, fieldValue)
      return (
        <div
          key={`preview-${widget.id}`}
          className="absolute overflow-hidden bg-white"
          style={{
            ...baseStyle,
            height: isFlowField ? "auto" : safeHeight,
            minHeight: isFlowField ? renderedFieldHeight : safeHeight,
            display: "flex",
            flexDirection: "column",
            justifyContent: alignByVertical,
            padding: contentPadding,
          }}
        >
          {hasFieldLabel ? (
            <div className={isFlowField ? "whitespace-pre-wrap break-words text-slate-500" : "truncate text-slate-500"} style={typographyStyle}>
              {fieldLabel}
            </div>
          ) : null}
          <div className={isFlowField ? "whitespace-pre-wrap break-words text-slate-900" : "truncate text-slate-900"} style={typographyStyle}>
            {fieldValue}
          </div>
        </div>
      )
    }

    if (widget.type === "image") {
      const bindingType = String(widget.props?.bindingType || "static")
      const staticUrl = String(widget.props?.staticUrl || "")
      const fieldKey = String(widget.props?.fieldKey || "")
      const imageFit = getWidgetImageFit(widget)
      const boundValue = getFieldDisplay(fieldKey, String(widget.props?.datasourceId || ""))
      const imageUrl =
        bindingType === "field"
          ? isLikelyImageUrl(boundValue)
            ? boundValue
            : ""
          : isLikelyImageUrl(staticUrl)
            ? staticUrl
            : ""

      return (
        <div
          key={`preview-${widget.id}`}
          className="absolute overflow-hidden bg-slate-100"
          style={baseStyle}
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={fieldKey || "report-image"}
              className="h-full w-full"
              style={{ objectFit: imageFit }}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center px-2 text-center text-xs text-slate-500">
              {bindingType === "field" ? `Image field: ${fieldKey || "-"}` : "Image placeholder"}
            </div>
          )}
        </div>
      )
    }

    if (widget.type === "line") {
      const line = getWidgetLineConfig(widget)
      return (
        <div
          key={`preview-${widget.id}`}
          className="absolute overflow-hidden bg-transparent"
          style={{
            ...baseStyle,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className="w-full"
            style={{
              borderTop: `${line.lineThickness}px ${line.lineStyle} ${line.lineColor}`,
              transform: `rotate(${line.lineAngle}deg)`,
              transformOrigin: "center",
            }}
          />
        </div>
      )
    }

    if (widget.type === "rectangle") {
      return (
        <div
          key={`preview-${widget.id}`}
          className="absolute overflow-hidden"
          style={{
            ...baseStyle,
            backgroundColor: String(widget.props?.fillColor || "transparent"),
          }}
        />
      )
    }

    if (widget.type === "table") {
      const columns = String(widget.props?.columns || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
      const rows =
        tableRows && tableRows.length > 0
          ? tableRows
          : columns.length > 0
            ? [columns.map(() => "-")]
            : [["-"]]
      const isFlowTable = getWidgetPositionMode(widget) === "flow"
      const renderedTableHeight = estimateTableRenderedHeight(widget, columns, rows)
      const columnWidths = getTableColumnWidths(widget, columns)
      const hasColumnWidths = columns.some((columnKey) => {
        const width = columnWidths[columnKey]
        return Number.isFinite(width) && width > 0
      })

      return (
        <div
          key={`preview-${widget.id}`}
          className="absolute overflow-visible bg-white"
          style={{
            ...baseStyle,
            height: "auto",
            minHeight: isFlowTable ? renderedTableHeight : safeHeight,
          }}
        >
          <table
            className="w-full border-collapse text-[11px]"
            style={{ tableLayout: hasColumnWidths ? "fixed" : "auto" }}
          >
            {columns.length > 0 ? (
              <colgroup>
                {columns.map((columnKey) => {
                  const width = columnWidths[columnKey]
                  return (
                    <col
                      key={`col-${widget.id}-${columnKey}`}
                      style={
                        Number.isFinite(width) && width > 0
                          ? { width: `${width}%` }
                          : undefined
                      }
                    />
                  )
                })}
              </colgroup>
            ) : null}
            <thead>
              <tr className="bg-slate-50">
                {columns.length === 0 ? (
                  <th className="border border-slate-200 px-2 py-1 text-left font-medium text-slate-600">
                    No columns
                  </th>
                ) : (
                  columns.map((columnKey) => (
                    <th
                      key={`head-${widget.id}-${columnKey}`}
                      className="border border-slate-200 px-2 py-1 text-left font-medium text-slate-600"
                    >
                      {getTableColumnLabel(widget, columnKey, fieldLabelMap, datasourcePreviewResults, reportDatasources)}
                    </th>
                  ))
                )}
              </tr>
            </thead>
            {columns.length > 0 ? (
              <tbody>
                {rows.map((rowValues, rowIndex) => (
                  <tr key={`row-${widget.id}-${rowIndex}`}>
                    {rowValues.map((cellValue, cellIndex) => (
                      <td
                        key={`cell-${widget.id}-${rowIndex}-${cellIndex}`}
                        className="border border-slate-200 px-2 py-1 text-slate-700 whitespace-normal break-words align-top"
                      >
                        {cellValue}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            ) : null}
          </table>
        </div>
      )
    }

    return null
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="text-lg">Permit Report Preview</CardTitle>
              <p className="text-sm text-slate-600">
                {permitDetail.permitNumber} | {reportTemplate.name}
              </p>
              <p className="text-xs text-slate-500">
                Rows: {reportRows.length} | Workers: {workers.length} | Template:{" "}
                {reportTemplate.code}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {onClose ? (
                <Button variant="outline" onClick={onClose}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {closeLabel}
                </Button>
              ) : null}
              <Button
                onClick={() => void handleExportPdf()}
                disabled={!previewEnabled || isExporting}
              >
                {isExporting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <FileDown className="mr-2 h-4 w-4" />
                )}
                Export PDF
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {!previewEnabled ? (
            <Alert>
              <AlertDescription>
                This template has no layout widgets. Configure layout in Dynamic Report Template
                Editor first.
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge variant="outline">
                Page {previewPageIndex + 1} / {previewTotalPages}
              </Badge>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setPreviewPageIndex((prev) => Math.max(0, prev - 1))}
                disabled={previewPageIndex <= 0}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() =>
                  setPreviewPageIndex((prev) =>
                    Math.min(Math.max(0, previewTotalPages - 1), prev + 1),
                  )
                }
                disabled={previewPageIndex >= previewTotalPages - 1}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <Button variant="outline" size="sm" onClick={() => void loadPreview()}>
              Refresh
            </Button>
          </div>

          <div className="overflow-auto rounded-lg border bg-slate-100 p-4">
            <div
              className="relative mx-auto bg-white shadow-sm"
              style={{
                width: fullCanvasMetrics.width,
                height: fullCanvasMetrics.height,
              }}
            >
              {frame.enabled ? (
                <div
                  className="absolute pointer-events-none"
                  style={{
                    left: frame.insetLeft,
                    top: frameTopOffset + frame.insetTop,
                    width: Math.max(
                      20,
                      fullCanvasMetrics.width - frame.insetLeft - frame.insetRight,
                    ),
                    height: Math.max(
                      20,
                      frameHeightScope - frame.insetTop - frame.insetBottom,
                    ),
                    borderColor: frame.borderColor,
                    borderWidth: Math.max(1, frame.borderWidth),
                    borderStyle: frame.borderStyle,
                    borderRadius: frame.cornerRadius,
                    boxSizing: "border-box",
                  }}
                />
              ) : null}

              {headerEnabled
                ? previewHeaderWidgets.map((widget) => renderWidget(widget, 0))
                : null}

              {previewBodyEntries.map((entry) =>
                renderWidget(entry.widget, bodyOffsetY, entry.renderY, entry.renderHeight, entry.tableRows),
              )}

              {footerEnabled
                ? previewFooterWidgets.map((widget) =>
                    renderWidget(widget, footerOffsetY),
                  )
                : null}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export function PermitReportPreviewPage() {
  const navigate = useNavigate()
  const { permitId = "" } = useParams<{ permitId: string }>()
  const [searchParams] = useSearchParams()
  const tenantId = searchParams.get("tenantId")?.trim() || DEFAULT_TENANT_ID
  const viewMode = searchParams.get("view")?.trim().toLowerCase() || "preview"
  const handleClose = () => {
    if (viewMode === "pdf" && window.history.length <= 1) {
      window.close()
      return
    }
    navigate(-1)
  }

  return (
    <PermitReportPreviewContent
      permitId={permitId}
      tenantId={tenantId}
      onClose={handleClose}
      closeLabel={viewMode === "pdf" ? "Close" : "Back"}
      renderPdfInline={viewMode === "pdf"}
    />
  )
}

export function PermitReportPdfPage() {
  const { permitId = "" } = useParams<{ permitId: string }>()
  const [searchParams] = useSearchParams()
  const tenantId = searchParams.get("tenantId")?.trim() || DEFAULT_TENANT_ID

  return (
    <PermitReportPreviewContent
      permitId={permitId}
      tenantId={tenantId}
      renderPdfInline
    />
  )
}
